import React, { useState, useEffect, useRef } from 'react';
import {
  ShoppingBagIcon,
  CurrencyDollarIcon,
  ShieldCheckIcon,
  CreditCardIcon,
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  BoltIcon
} from '@heroicons/react/24/outline';
import socketService from '../services/socket';

function ActivityFeed() {
  const [activities, setActivities] = useState([]);
  const [showFeed, setShowFeed] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const feedRef = useRef(null);

  useEffect(() => {
    socketService.connect();

    const handleRecentActivity = (recent) => {
      setActivities(recent);
    };

    const handleNewCookie = (data) => {
      const newActivity = {
        id: Date.now(),
        type: 'cookie',
        icon: ShoppingBagIcon,
        color: '#00ffff',
        bgColor: 'rgba(0,255,255,0.1)',
        title: '🍪 Nueva Cookie',
        description: `$${data.amount} - ${data.service || 'desconocido'}`,
        time: new Date().toLocaleTimeString(),
        data
      };
      
      setActivities(prev => [newActivity, ...prev].slice(0, 50));
      if (!showFeed) setUnreadCount(prev => prev + 1);
      
      // Auto-scroll al inicio si el feed está abierto
      if (showFeed && feedRef.current) {
        feedRef.current.scrollTop = 0;
      }
    };

    const handlePayment = (data) => {
      const newActivity = {
        id: Date.now(),
        type: 'payment',
        icon: data.success ? CheckCircleIcon : XCircleIcon,
        color: data.success ? '#ccff00' : '#ff0033',
        bgColor: data.success ? 'rgba(204,255,0,0.1)' : 'rgba(255,0,51,0.1)',
        title: data.success ? '💰 Pago Exitoso' : '❌ Pago Fallido',
        description: `$${data.amount} - ${data.cookieName}`,
        time: new Date().toLocaleTimeString(),
        data
      };
      
      setActivities(prev => [newActivity, ...prev].slice(0, 50));
      if (!showFeed) setUnreadCount(prev => prev + 1);
    };

    const handleVerification = (data) => {
      const newActivity = {
        id: Date.now(),
        type: 'verification',
        icon: ShieldCheckIcon,
        color: data.verified ? '#ccff00' : '#ff0033',
        bgColor: data.verified ? 'rgba(204,255,0,0.1)' : 'rgba(255,0,51,0.1)',
        title: data.verified ? '🔍 Cookie Verificada' : '🔍 Verificación Fallida',
        description: data.cookieName,
        time: new Date().toLocaleTimeString(),
        data
      };
      
      setActivities(prev => [newActivity, ...prev].slice(0, 50));
      if (!showFeed) setUnreadCount(prev => prev + 1);
    };

    const handleCardAdded = (data) => {
      const newActivity = {
        id: Date.now(),
        type: 'card',
        icon: CreditCardIcon,
        color: '#ff6600',
        bgColor: 'rgba(255,102,0,0.1)',
        title: '💳 Tarjeta Agregada',
        description: `${data.cardBrand} •••• ${data.cardLastFour} - ${data.cookieName}`,
        time: new Date().toLocaleTimeString(),
        data
      };
      
      setActivities(prev => [newActivity, ...prev].slice(0, 50));
      if (!showFeed) setUnreadCount(prev => prev + 1);
    };

    socketService.on('recent-activity', handleRecentActivity);
    socketService.on('new-cookie', handleNewCookie);
    socketService.on('payment', handlePayment);
    socketService.on('verification', handleVerification);
    socketService.on('card-added', handleCardAdded);

    return () => {
      socketService.off('recent-activity', handleRecentActivity);
      socketService.off('new-cookie', handleNewCookie);
      socketService.off('payment', handlePayment);
      socketService.off('verification', handleVerification);
      socketService.off('card-added', handleCardAdded);
    };
  }, [showFeed]);

  const handleOpenFeed = () => {
    setShowFeed(true);
    setUnreadCount(0);
  };

  if (!showFeed) {
    return (
      <button
        onClick={handleOpenFeed}
        style={{
          position: 'fixed',
          bottom: '6rem',
          right: '1rem',
          background: '#111111',
          border: `1px solid ${unreadCount > 0 ? '#00ffff' : '#222222'}`,
          borderRadius: '3rem',
          padding: '0.75rem 1.5rem',
          color: unreadCount > 0 ? '#00ffff' : '#9ca3af',
          fontWeight: '600',
          zIndex: 100,
          boxShadow: unreadCount > 0 ? '0 0 20px rgba(0,255,255,0.3)' : 'none',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          gap: '0.5rem',
          transition: 'all 0.3s'
        }}
      >
        <BoltIcon style={{ width: '1.25rem', height: '1.25rem' }} />
        <span>Actividad en Vivo</span>
        {unreadCount > 0 && (
          <span style={{
            background: '#00ffff',
            color: '#000000',
            borderRadius: '50%',
            width: '1.5rem',
            height: '1.5rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '0.75rem',
            fontWeight: 'bold'
          }}>
            {unreadCount}
          </span>
        )}
      </button>
    );
  }

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      maxWidth: '450px',
      background: '#0a0a0a',
      borderLeft: '1px solid #222222',
      zIndex: 1000,
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '-5px 0 30px rgba(0,0,0,0.8)',
      animation: 'slideIn 0.3s ease-out'
    }}>
      <style>
        {`
          @keyframes slideIn {
            from { transform: translateX(100%); }
            to { transform: translateX(0); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.5; }
          }
          
          .activity-item {
            animation: slideIn 0.3s ease-out;
          }
        `}
      </style>

      {/* Header */}
      <div style={{
        padding: '1.5rem',
        borderBottom: '1px solid #222222',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        background: '#111111'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <BoltIcon style={{ width: '1.5rem', height: '1.5rem', color: '#00ffff' }} />
          <div>
            <h3 style={{ color: '#ffffff', fontSize: '1.25rem', fontWeight: '600' }}>
              Actividad en Vivo
            </h3>
            <p style={{ color: '#9ca3af', fontSize: '0.75rem' }}>
              {activities.length} eventos • Tiempo real
            </p>
          </div>
        </div>
        <button
          onClick={() => setShowFeed(false)}
          style={{
            background: 'none',
            border: 'none',
            color: '#9ca3af',
            fontSize: '1.5rem',
            cursor: 'pointer',
            width: '2rem',
            height: '2rem',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            borderRadius: '50%',
            transition: 'all 0.2s'
          }}
          onMouseEnter={(e) => e.target.style.background = '#222222'}
          onMouseLeave={(e) => e.target.style.background = 'none'}
        >
          ✕
        </button>
      </div>

      {/* Feed */}
      <div
        ref={feedRef}
        style={{
          flex: 1,
          overflowY: 'auto',
          padding: '1rem'
        }}
      >
        {activities.length === 0 ? (
          <div style={{
            textAlign: 'center',
            padding: '3rem 1rem',
            color: '#9ca3af',
            animation: 'pulse 2s infinite'
          }}>
            <ClockIcon style={{ width: '3rem', height: '3rem', margin: '0 auto 1rem', opacity: 0.5 }} />
            <p>Esperando actividad...</p>
            <p style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#00ffff' }}>
              Las nuevas acciones aparecerán aquí en tiempo real
            </p>
          </div>
        ) : (
          activities.map((activity, index) => {
            const Icon = activity.icon;
            return (
              <div
                key={activity.id || index}
                className="activity-item"
                style={{
                  background: '#111111',
                  border: `1px solid ${activity.color}20`,
                  borderRadius: '1rem',
                  padding: '1rem',
                  marginBottom: '0.75rem',
                  transition: 'all 0.3s',
                  cursor: 'pointer',
                  borderLeft: `4px solid ${activity.color}`
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#1a1a1a'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#111111'}
              >
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{
                    width: '2.5rem',
                    height: '2.5rem',
                    borderRadius: '0.75rem',
                    background: activity.bgColor,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon style={{ width: '1.25rem', height: '1.25rem', color: activity.color }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                      <span style={{ fontWeight: '600', color: '#ffffff' }}>{activity.title}</span>
                      <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>{activity.time}</span>
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#9ca3af' }}>{activity.description}</p>
                    
                    {/* Detalles adicionales */}
                    {activity.type === 'cookie' && activity.data && (
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem',
                        background: '#1a1a1a',
                        borderRadius: '0.5rem',
                        fontSize: '0.7rem',
                        color: '#00ffff'
                      }}>
                        Usos restantes: {activity.data.remainingUses}/{activity.data.maxUses}
                      </div>
                    )}
                    
                    {activity.type === 'payment' && activity.data && activity.data.transactionId && (
                      <div style={{
                        marginTop: '0.5rem',
                        padding: '0.5rem',
                        background: '#1a1a1a',
                        borderRadius: '0.5rem',
                        fontSize: '0.7rem',
                        color: activity.data.success ? '#ccff00' : '#ff0033',
                        fontFamily: 'monospace'
                      }}>
                        ID: {activity.data.transactionId}
                      </div>
                    )}
                    
                    {activity.type === 'verification' && activity.data && (
                      <div style={{
                        marginTop: '0.5rem',
                        display: 'flex',
                        gap: '1rem',
                        fontSize: '0.7rem',
                        color: '#9ca3af'
                      }}>
                        {activity.data.responseTime && (
                          <span>⏱️ {activity.data.responseTime}ms</span>
                        )}
                        {activity.data.statusCode && (
                          <span>📊 HTTP {activity.data.statusCode}</span>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Footer con stats rápidas */}
      <div style={{
        padding: '1rem',
        borderTop: '1px solid #222222',
        background: '#111111',
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '0.5rem',
        fontSize: '0.75rem',
        color: '#9ca3af'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#00ffff', fontWeight: 'bold' }}>
            {activities.filter(a => a.type === 'cookie').length}
          </div>
          <div>Cookies</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#ccff00', fontWeight: 'bold' }}>
            {activities.filter(a => a.type === 'payment' && a.data?.success).length}
          </div>
          <div>Pagos OK</div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ color: '#ff00ff', fontWeight: 'bold' }}>
            {activities.filter(a => a.type === 'card').length}
          </div>
          <div>Tarjetas</div>
        </div>
      </div>
    </div>
  );
}

export default ActivityFeed;